import React, { useState, useEffect } from "react";
import Modal from "../base/Modal";
import toast from "react-hot-toast";
import { API_URL } from "../../services/api";
import { DataState } from "../../context/DataProvider";

const JiraSynchronizationModal = ({ isOpen, onClose }) => {
    const { userGoogleId } = DataState();
    const [syncData, setSyncData] = useState({ projects: [], users: 0 });
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});

    const fetchSyncData = async () => {
        if (!userGoogleId) return;
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/auth/jira/getCounts`, {
                headers: { Authorization: `Bearer ${userGoogleId}` },
                credentials: "include"
            });
            if (!response.ok) throw new Error("Failed to get sync counts.");
            const data = await response.json();
            setSyncData(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchSyncData();
        }
    }, [isOpen, userGoogleId]);

    const handleAction = async (action, projectKey) => {
        const endpoint = action === 'sync' ? 'synchronize' : 'clear';
        const method = action === 'sync' ? 'POST' : 'DELETE';
        const loadingKey = `${projectKey}_${action}`;

        setActionLoading(prev => ({ ...prev, [loadingKey]: true }));
        try {
            const response = await fetch(`${API_URL}/jira_issues_and_comments/project/${endpoint}`, {
                method: method,
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${userGoogleId}` },
                body: JSON.stringify({ project_key: projectKey }),
                credentials: "include"
            });
            if (!response.ok) {
                 const err = await response.json();
                 throw new Error(err.detail || `Failed to ${action} data.`);
            }
            toast.success(`Action for project ${projectKey} initiated!`);
            await fetchSyncData(); // Refresh data
        } catch (error) {
            toast.error(error.message);
        } finally {
            setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };
    
    return (
        <Modal title="Jira Synchronization Status" isOpen={isOpen} onClose={onClose}>
            <div className="p-6 space-y-4">
                {loading ? (
                    <p className="text-center text-zinc-400">Loading Status...</p>
                ) : !syncData.projects || syncData.projects.length === 0 ? (
                    <p className="text-center text-zinc-400">No projects are selected for synchronization.</p>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {syncData.projects.map(project => (
                            <div key={project.key} className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                                <p className="font-semibold text-zinc-200">{project.name} ({project.key})</p>
                                <div className="text-sm text-zinc-400 my-2">
                                    <span>Issues: <span className="text-zinc-200">{project.issues}</span></span> | 
                                    <span> Comments: <span className="text-zinc-200">{project.comments}</span></span>
                                </div>
                                <div className="flex justify-end gap-2 mt-2">
                                    <button
                                        onClick={() => handleAction('clear', project.key)}
                                        disabled={actionLoading[`${project.key}_clear`]}
                                        className="text-xs bg-red-800 hover:bg-red-700 text-white font-semibold py-1 px-3 rounded-md disabled:opacity-50"
                                    >
                                        {actionLoading[`${project.key}_clear`] ? 'Clearing...' : 'Clear Data'}
                                    </button>
                                    <button
                                        onClick={() => handleAction('sync', project.key)}
                                        disabled={actionLoading[`${project.key}_sync`]}
                                        className="text-xs bg-blue-600 hover:bg-[#5652CF] text-white font-semibold py-1 px-3 rounded-md disabled:opacity-50"
                                    >
                                        {actionLoading[`${project.key}_sync`] ? 'Syncing...' : 'Synchronize'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default JiraSynchronizationModal;